import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSEO } from "@/hooks/useSEO";
import { BRAND_HANDLE, BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { BaseTemplate } from "@/components/templates/BaseTemplate";

/**
 * Medellín — Public SEO landing page using BaseTemplate (rich layout + admin control).
 * Supports /medellin and /en/medellin. Uses Stripe (CO). Full BaseTemplate experience + admin overrides like AR regions.
 */
const Medellin = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const isEn = location.pathname.startsWith("/en/") || location.pathname.startsWith("/en");

  useEffect(() => {
    if (isEn && i18n.language?.split("-")[0] !== "en") {
      void i18n.changeLanguage("en");
    }
  }, [isEn, i18n]);

  const locale = isEn ? "en_US" : "es_CO";
  const title = isEn
    ? `${BRAND_PERSON_NAME} | Medellín — Content Creator Colombia | Exclusive Access`
    : `${BRAND_PERSON_NAME} | Medellín — Creadora de Contenido Colombia | Acceso Exclusivo`;
  const description = isEn
    ? `Join ${BRAND_PERSON_NAME} in Medellín & Colombia. Premium fitness, lifestyle and beauty content. Private community via Stripe.`
    : `Únete a ${BRAND_PERSON_NAME} en Medellín y Colombia. Contenido premium fitness, lifestyle y belleza. Comunidad privada vía Stripe.`;
  const keywords = isEn
    ? `${BRAND_PERSON_NAME}, medellin, medellín, content creator medellin, colombia premium, fitness medellin, stripe membership`
    : `${BRAND_PERSON_NAME}, medellin, medellín, creadora medellin, colombia premium, fitness medellin, membresia stripe`;

  useSEO({
    title,
    description,
    keywords,
    ogImage: `${BRAND_SITE_URL}/og.png`,
    ogTitle: title,
    ogDescription: description,
    canonical: `${BRAND_SITE_URL}${location.pathname.replace(/\/$/, "") || "/medellin"}`,
    author: BRAND_PERSON_NAME,
    locale,
    alternateLocale: isEn ? "es_CO" : "en_US",
    type: "website",
    publishedTime: "2025-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
  });

  const heroTitle = isEn ? `${BRAND_PERSON_NAME} in Medellín` : `${BRAND_PERSON_NAME} Medellín`;
  const heroDescription = isEn
    ? "Exclusive premium content for Medellín. Private access with Stripe checkout."
    : "Contenido premium exclusivo para Medellín. Acceso privado con checkout Stripe.";

  return (
    <BaseTemplate
      heroTitle={heroTitle}
      heroDescription={heroDescription}
      showSobreMi={false}
      showPortfolioNavLink
      showGalleryNavLink
      showContactNavLink
      showGallerySection
      showTallySection
    />
  );
};

export default Medellin;
